import { el, fmt, type Nav, type Screen } from "./dom";
import { buildTopbar } from "./topbar";
import { i18n, t } from "../i18n";
import {
  fetchLeaderboard,
  playerHandle,
  type LeaderCols,
  type LeaderEntry,
  type LeaderPeriod,
} from "../net/sync";
import { todayKey } from "../game/daily";
import { progress } from "../store/progress";
import { settings } from "../store/settings";

type Tab = "endless" | "daily";

const PERIODS: ReadonlyArray<{ id: LeaderPeriod; key: string }> = [
  { id: "all", key: "lb.period.all" },
  { id: "month", key: "lb.period.month" },
  { id: "week", key: "lb.period.week" },
  { id: "today", key: "lb.period.today" },
];

const WIDTHS: ReadonlyArray<{ id: LeaderCols; label: string }> = [
  { id: 0, label: "lb.cols.all" },
  { id: 4, label: "lb.cols.4" },
  { id: 5, label: "lb.cols.5" },
  { id: 6, label: "lb.cols.6" },
];

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!,
  );
}

export class LeaderboardScreen implements Screen {
  private off?: () => void;
  private tab: Tab = "endless";
  private period: LeaderPeriod = "all";
  private cols: LeaderCols;
  private body!: HTMLElement;
  private periodBar!: HTMLElement;
  private widthBar!: HTMLElement;
  constructor(private nav: Nav) {
    // Default the cols filter to whatever the player currently has set on the
    // home screen. They'll see "their" board's leaderboard first.
    const setting = progress.effectiveCols(settings.get("boardCols"));
    this.cols = (setting === 4 || setting === 5 || setting === 6 ? setting : 0) as LeaderCols;
  }

  mount(host: HTMLElement): void {
    const screen = el("div", "screen leaderboard");
    const top = buildTopbar({ title: "menu.leaderboard", onBack: () => this.nav.home() });

    const tabs = el("div", "lb-tabs");
    const mkTab = (tab: Tab, key: string): HTMLElement => {
      const b = el("button", `lb-tab ${tab === this.tab ? "active" : ""}`, t(key));
      b.onclick = () => {
        this.tab = tab;
        tabs.querySelectorAll(".lb-tab").forEach((e) => e.classList.remove("active"));
        b.classList.add("active");
        void this.load();
      };
      return b;
    };
    tabs.append(mkTab("endless", "menu.endless"), mkTab("daily", "menu.daily"));

    this.periodBar = el("div", "lb-periods");
    const mkPeriod = (p: LeaderPeriod, key: string): HTMLElement => {
      const b = el("button", `lb-period ${p === this.period ? "active" : ""}`, t(key));
      b.onclick = () => {
        this.period = p;
        this.periodBar.querySelectorAll(".lb-period").forEach((e) => e.classList.remove("active"));
        b.classList.add("active");
        void this.load();
      };
      return b;
    };
    this.periodBar.append(...PERIODS.map((p) => mkPeriod(p.id, p.key)));

    this.widthBar = el("div", "lb-widths");
    const mkWidth = (w: LeaderCols, key: string): HTMLElement => {
      const b = el("button", `lb-width ${w === this.cols ? "active" : ""}`, t(key));
      b.onclick = () => {
        this.cols = w;
        this.widthBar.querySelectorAll(".lb-width").forEach((e) => e.classList.remove("active"));
        b.classList.add("active");
        void this.load();
      };
      return b;
    };
    this.widthBar.append(...WIDTHS.map((w) => mkWidth(w.id, w.label)));

    this.body = el("div", "lb-body");
    screen.append(top.node, tabs, this.periodBar, this.widthBar, this.body);
    host.appendChild(screen);

    const refresh = (): void => {
      top.refresh();
      tabs.querySelectorAll(".lb-tab").forEach((b, i) => {
        b.textContent = t(i === 0 ? "menu.endless" : "menu.daily");
      });
      this.periodBar.querySelectorAll(".lb-period").forEach((b, i) => {
        b.textContent = t(PERIODS[i].key);
      });
      this.widthBar.querySelectorAll(".lb-width").forEach((b, i) => {
        b.textContent = t(WIDTHS[i].label);
      });
      void this.load();
    };
    refresh();
    this.off = i18n.onChange(refresh);
  }

  private async load(): Promise<void> {
    this.body.innerHTML = `<div class="lb-loading">${t("lb.loading")}</div>`;
    const mode = this.tab;
    // Empty ref + a competitive period works for both boards: endless runs all
    // share ref="", and the daily board aggregates across days by created_at.
    const entries = await fetchLeaderboard(mode, "", this.period, this.cols);

    const localBest =
      mode === "endless"
        ? progress.bestEndlessFor(this.cols)
        : progress.snapshot.dailyBest[todayKey()] ?? 0;

    if (entries === null) {
      this.body.innerHTML = `
        <div class="lb-offline">${t("lb.offline")}</div>
        <div class="lb-local"><span>${t("lb.yourBest")}</span><span>${fmt(localBest)}</span></div>`;
      return;
    }
    this.render(entries, localBest);
  }

  private render(entries: LeaderEntry[], localBest: number): void {
    this.body.innerHTML = "";
    const me = playerHandle();
    const local = el("div", "lb-local");
    local.innerHTML = `<span>${t("lb.yourBest")}</span><span>${fmt(localBest)}</span>`;
    this.body.appendChild(local);

    if (entries.length === 0) {
      this.body.appendChild(el("div", "lb-empty", t("lb.empty")));
      return;
    }
    const list = el("div", "lb-list");
    entries.forEach((e, i) => {
      const mine = e.name === me ? "me" : "";
      const row = el("div", `lb-row ${i < 3 ? `top${i + 1}` : ""} ${mine}`);
      // Titles travel as achievement ids and are localized at render time.
      // Unknown ids (e.g. from a newer client) are silently hidden.
      const titleName = e.title ? t(`ach.${e.title}`) : "";
      const titleChip =
        e.title && titleName !== `ach.${e.title}`
          ? `<span class="lb-title">${escapeHtml(titleName)}</span>`
          : "";
      row.innerHTML = `
        <span class="lb-rank">${i + 1}</span>
        <span class="lb-name">${e.name ? escapeHtml(e.name) : "—"}${titleChip}</span>
        <span class="lb-score">${fmt(e.score)}</span>`;
      list.appendChild(row);
    });
    this.body.appendChild(list);
  }

  unmount(): void {
    this.off?.();
  }
}
